import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { MultiSelect } from 'components/ui/multi-select';
import { Skeleton } from 'components/ui/skeleton';
import { useToast } from 'components/ui/toast';
import AppContext from 'contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from 'utils/permissionUtils';
import { getAllPermissions, getDefaultPermissions, usePermissionLabel } from 'constants/permissions';
import { apiErrorMessage } from 'utils/errorMessage';
import { useRoleLabel } from 'utils/roleUtils';
import { useServiceDesks } from 'hooks/useServiceDesks';
import { useCreationRights, useUserRights, useUpdateUserRights } from 'hooks/useUserRights';
import type { DeskAssignment, UserRights } from 'api/usersApi';

interface RightsSectionProps {
  /** The account to read and write. `null` while creating, where no row exists yet. */
  userId: number | null;
  /** Creation only: the rights the record's form holds, and the way to patch them. */
  value?: UserRights;
  onChange?: (patch: Partial<UserRights>) => void;
}

/**
 * The account rights the L2a record left behind: the desks this technician reads, the desks he may
 * open a ticket on, and the permissions added to his role. On an existing record they live on
 * `users`, so the card reads and writes `/users/:id` — a partial PUT that leaves the business
 * record alone — with an edit mode of its own rather than hiding behind a "Save the record" that
 * would not send them. On a creation there is no row to write: the same controls edit the form's
 * own draft, and the create call carries it.
 */
const RightsSection = ({ userId, value, onChange }: RightsSectionProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const permissionLabel = usePermissionLabel();
  const roleLabel = useRoleLabel();
  const { currentUser } = useContext(AppContext);

  const creating = userId === null;

  // `/users/:id`, `/users/abilities` and `/service-desks/all` carry permissions of their own:
  // without them the card would render an error where the reader can do nothing about it.
  const canRead = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_USERS_VIEW);
  const canWrite = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_USERS_MANAGE);
  // `/service-desks/all` takes either of these two, and its own guard lists them: asking for
  // less would leave a reader who may call it looking at `#3` where a desk has a name.
  const canNameDesks = canWrite || hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_ISSUES_VIEW);

  const { data: record, isLoading, error } = useUserRights(canRead ? userId : null);
  const { data: creationRights } = useCreationRights(creating && canRead);
  const { serviceDesks } = useServiceDesks({ enabled: canNameDesks });
  const saveRights = useUpdateUserRights(userId);

  const [draft, setDraft] = useState<UserRights | null>(null);

  if (!canRead) return null;

  // One shape either way: the creation abilities carry the three keys the card actually reads.
  const abilities = creating ? creationRights : record?.abilities;
  const rights = creating ? value : record?.rights;
  // What the controls edit: the form's draft on a creation, the card's own buffer otherwise.
  const editable = creating ? value : draft;
  const deskOptions = serviceDesks.map((desk) => ({ value: String(desk.id), label: desk.name }));

  const deskName = (id: number | '*'): string =>
    id === '*' ? t('All Service Desks') : serviceDesks.find((desk) => desk.id === id)?.name ?? `#${id}`;

  const deskSummary = (desks: DeskAssignment): string =>
    desks.length === 0 ? t('No service desk') : desks.map(deskName).join(', ');

  /** Never offer a grant the API refuses, but keep what the account already holds visible. */
  const roleDefaults: string[] = getDefaultPermissions(creating ? 'technician' : record?.role ?? '');
  const permissionOptions = getAllPermissions()
    .filter(({ value: permission }: { value: string }) => !roleDefaults.includes(permission))
    .filter(
      ({ value: permission }: { value: string }) =>
        (abilities?.grantablePermissions ?? []).includes(permission) ||
        (rights?.permissions ?? []).includes(permission)
    )
    .map(({ value: permission }: { value: string }) => ({ value: permission, label: permissionLabel(permission) }));

  const patch = (values: Partial<UserRights>) => {
    if (creating) return onChange?.(values);
    return setDraft((current) => (current ? { ...current, ...values } : current));
  };

  // The wildcard is exclusive: held beside a desk it would claim two scopes, and the API refuses it.
  const pickDesks = (values: string[]) =>
    patch({ serviceDeskId: values.includes('*') ? ['*'] : values.map(Number) });

  const save = () => {
    if (!draft || saveRights.isPending) return;
    saveRights.mutate(draft, {
      onSuccess: () => {
        setDraft(null);
        toast.success(t('Rights saved'));
      },
      onError: (mutationError) => toast.error(apiErrorMessage(t, mutationError)),
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          {t('Rights and access')}
        </CardTitle>
        {!creating && record?.abilities.canEdit && canWrite && !draft && (
          <Button type="button" variant="outline" size="sm" onClick={() => setDraft(record.rights)}>
            <Pencil className="mr-1.5 h-4 w-4" aria-hidden="true" />
            {t('Edit the rights')}
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {!creating && isLoading && <Skeleton className="h-24 w-full" />}

        {!creating && !isLoading && (error || !record || !rights || !abilities) && (
          <p className="text-sm text-rose-600 dark:text-rose-400">
            {error ? apiErrorMessage(t, error) : t('The rights could not be loaded.')}
          </p>
        )}

        {rights && abilities && (
          <>
            {creating && (
              <p className="text-xs text-muted-foreground">
                {t('New technicians take the values below. Change them only if this one differs.')}
              </p>
            )}

            {!creating && record && (
              <div className="space-y-1.5">
                <Label>{t('Role')}</Label>
                <p className="text-sm text-zinc-900 dark:text-zinc-50">{roleLabel(record.role)}</p>
                <p className="text-xs text-muted-foreground">
                  {t('The role is set from the users screen: changing it here would take the record out of the technicians directory.')}
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>{t('Service desks')}</Label>
              {editable && abilities.canEditServiceDesks ? (
                <MultiSelect
                  aria-label={t('Service desks')}
                  options={deskOptions}
                  selected={editable.serviceDeskId.map(String)}
                  onChange={pickDesks}
                  placeholder={t('Select the Service Desks…')}
                  allOption={{ value: '*', label: t('All Service Desks') }}
                />
              ) : (
                <>
                  <p className="text-sm text-zinc-900 dark:text-zinc-50">{deskSummary(rights.serviceDeskId)}</p>
                  {editable && (
                    <p className="text-xs text-muted-foreground">{t('The Service Desk cannot be changed by an admin')}</p>
                  )}
                </>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>{t('Allowed service desks for ticket creation')}</Label>
              {editable && abilities.canEditServiceDesks ? (
                <MultiSelect
                  aria-label={t('Allowed service desks for ticket creation')}
                  options={deskOptions}
                  selected={editable.allowedTicketCreationDesks.map(String)}
                  onChange={(values) => patch({ allowedTicketCreationDesks: values.map(Number) })}
                  placeholder={t('Select the Service Desks…')}
                />
              ) : (
                <p className="text-sm text-zinc-900 dark:text-zinc-50">
                  {deskSummary(rights.allowedTicketCreationDesks)}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('Select the Service Desks on which this technician can create tickets')}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>{t('Additional permissions')}</Label>
              {editable && abilities.canGrantPermissions ? (
                permissionOptions.length > 0 ? (
                  <MultiSelect
                    aria-label={t('Additional permissions')}
                    options={permissionOptions}
                    selected={editable.permissions}
                    onChange={(values) => patch({ permissions: values })}
                    placeholder={t('Select additional permissions…')}
                  />
                ) : (
                  <p className="text-sm italic text-muted-foreground">
                    {t('This role already has all available permissions.')}
                  </p>
                )
              ) : (
                <>
                  <p className="text-sm text-zinc-900 dark:text-zinc-50">
                    {rights.permissions.length > 0 ? rights.permissions.map(permissionLabel).join(', ') : t('none')}
                  </p>
                  {editable && (
                    <p className="text-xs text-muted-foreground">{t('Only a super admin can change permissions')}</p>
                  )}
                </>
              )}
            </div>

            {!creating && draft && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDraft(null)} disabled={saveRights.isPending}>
                  {t('Cancel')}
                </Button>
                <Button type="button" onClick={save} disabled={saveRights.isPending}>
                  {saveRights.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                  {t('Save the rights')}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RightsSection;
